import React from 'react';
import { Link } from 'react-router-dom';
import { Server, Monitor, Cpu } from 'lucide-react';
import type { Runner } from '../types/runner';

function statusDot(status: string) {
  if (status === 'online') return 'bg-green-500';
  if (status === 'busy') return 'bg-amber-500';
  return 'bg-slate-300';
}

function statusLabel(status: string) {
  if (status === 'online') return 'Online';
  if (status === 'busy') return 'Busy';
  return 'Offline';
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function platformIcon(platform?: string) {
  if (!platform) return Monitor;
  if (platform === 'darwin') return Monitor;
  if (platform.startsWith('win')) return Monitor;
  return Server;
}

export default function RunnerCard({ runner }: { runner: Runner }) {
  return (
    <Link
      to={`/runners/${runner.id}`}
      className="group flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-all hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
          {React.createElement(platformIcon(runner.osInfo?.platform), { className: "h-5 w-5 text-slate-600" })}
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${statusDot(runner.status)}`} />
          <span className="text-xs text-slate-500">{statusLabel(runner.status)}</span>
        </div>
      </div>

      <h3 className="mt-3 font-semibold text-slate-900">{runner.name}</h3>
      <p className="mt-0.5 truncate text-xs text-slate-400">{runner.hostname}</p>

      {runner.osInfo && (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
          <Cpu className="h-3 w-3" />
          <span>
            {runner.osInfo.platform} / {runner.osInfo.arch}
          </span>
          {runner.osInfo.nodeVersion && (
            <span className="text-slate-300">node {runner.osInfo.nodeVersion}</span>
          )}
        </div>
      )}

      {runner.capabilities && runner.capabilities.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {runner.capabilities.map((cap) => (
            <span
              key={cap}
              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                cap === 'local-llm'
                  ? 'bg-purple-100 text-purple-700'
                  : cap === 'p2p'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-slate-100 text-slate-600'
              }`}
            >
              {cap}
            </span>
          ))}
        </div>
      )}

      <div className="mt-auto border-t border-slate-100 pt-3 text-xs text-slate-400" style={{ marginTop: 'auto', paddingTop: '0.75rem' }}>
        <div className="flex items-center justify-between">
          <span>
            {runner.assignedCount} / {runner.maxInstances} instances
          </span>
          <span>Seen {relativeTime(runner.lastSeenAt)}</span>
        </div>
      </div>
    </Link>
  );
}
